/**
 * AssetSearchModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContainer';
import './AssetSearchModal.css';

const AssetSearchModal = ({ isOpen, onClose, onSelectAsset, fileType = null }) => {
  const { showError } = useToast();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    file_type: fileType || '',
    search: '',
    category: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [categories, setCategories] = useState([]);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`/api/assets?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch assets');
      }

      const data = await response.json();
      setAssets(data.assets || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching assets:', error);
      showError('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, showError]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/assets/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      fetchCategories();
    }
  }, [isOpen, fetchAssets, fetchCategories]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleAssetSelect = (asset) => {
    onSelectAsset(asset);
    onClose();
  };

  const getAssetUrl = (asset) => {
    return `/api/assets/${asset.id}/download`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return '🖼️';
      case 'music':
        return '🎵';
      case 'video':
        return '🎬';
      case 'document':
        return '📄';
      default:
        return '📁';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="asset-search-modal-overlay">
      <div className="asset-search-modal">
        <div className="asset-search-modal-header">
          <h3>Insert Asset</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="asset-search-modal-content">
          {/* Filters */}
          <div className="asset-filters">
            <div className="filter-group">
              <label>Type:</label>
              <select 
                value={filters.file_type} 
                onChange={(e) => handleFilterChange('file_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="image">Images</option>
                <option value="music">Music</option>
                <option value="video">Videos</option>
                <option value="document">Documents</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Category:</label>
              <select 
                value={filters.category} 
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Search:</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search assets..."
              />
            </div>
          </div>

          {/* Assets Grid */}
          <div className="assets-grid">
            {loading ? (
              <div className="loading">Loading assets...</div>
            ) : assets.length === 0 ? (
              <div className="no-assets">No assets found</div>
            ) : (
              assets.map(asset => (
                <div 
                  key={asset.id} 
                  className="asset-item"
                  onClick={() => handleAssetSelect(asset)}
                >
                  <div className="asset-preview">
                    {asset.file_type === 'image' ? (
                      <img 
                        src={getAssetUrl(asset)} 
                        alt={asset.alt_text || asset.original_filename}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="asset-icon">
                        {getFileIcon(asset.file_type)}
                      </div>
                    )}
                    <div className="asset-fallback" style={{ display: 'none' }}>
                      {getFileIcon(asset.file_type)}
                    </div>
                  </div>
                  <div className="asset-info">
                    <div className="asset-name" title={asset.original_filename}>
                      {asset.original_filename}
                    </div>
                    <div className="asset-meta">
                      <span className="asset-type">{asset.file_type}</span>
                      <span className="asset-size">{formatFileSize(asset.file_size)}</span>
                    </div>
                    {asset.description && (
                      <div className="asset-description" title={asset.description}>
                        {asset.description}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button 
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button 
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetSearchModal;
